import { Grid, IconButton, Typography, useEventCallback } from '@mui/material';
import { Fragment, memo, useMemo } from 'react';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import {
  Annotations,
  DataWizardAct,
  LastStepVM,
  ParentVM,
  SectionVM,
} from 'civil-status/view-models/step-form';
import { ItemTemplateVM } from '../list/list-template-transcription';
import { AddCircleOutline } from '@mui/icons-material';
import FormAnnotation from './form';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import ListTemplateAnnotations from '../list/list-template-annotations';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { DateTime } from 'luxon';
import { PmiButton } from 'shell/components/shared/button';
import { KeySubjects } from 'civil-status/constants/keys-subjects-section';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { HttpResponse } from 'shell/models/http-response';
import { AxiosResponse } from 'axios';

interface AnnotationsDataProps {
  formContext: FormikProps<LastStepVM>;
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  onSave: (values: SectionVM) => ResponsePromise<DataWizardAct> | void | undefined;
  configuration?: string;
  tipoEvento?: string;
  idEvento?: number;
}

const AnnotationsDataComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  onChanges,
  onSave,
  configuration,
  tipoEvento,
  idEvento,
  formContext,
}: AnnotationsDataProps): React.ReactElement => {
  const isAnncont105 = useMemo(() => {
    return configuration === ConfigurationType.ANNCONT105;
  }, [configuration]);

  const isAnncont312 = useMemo(() => {
    return configuration === ConfigurationType.ANNCONT312;
  }, [configuration]);

  const handleListTemplateTranscriptionChange = useEventCallback((value?: ItemTemplateVM) => {
    onChange('contextualAnnotationTemplate', value);
  });

  const onAddAnnotation = useEventCallback(() => {
    if (!values?.contextualAnnotationTemplate?.id) {
      return;
    }

    const newValueAnnotation: Annotations = {
      isNotCertifiable: false,
      textContextualAnnotation: values?.contextualAnnotationTemplate?.valueTemplate || '',
      typeContextualAnnotation: values?.contextualAnnotationTemplate?.value || '',
    };

    onChanges({
      contextualAnnotationTemplate: undefined,
      annotations: (values.annotations || []).concat([
        newValueAnnotation,
      ]) as unknown as Array<Annotations>,
    });
  });

  const onChangeAnnotation = useEventCallback(
    (index: number, key: keyof Annotations, value: Annotations[keyof Annotations]) => {
      const newValues = (values?.annotations || []).slice();

      newValues[index] = {
        ...newValues[index],
        [key]: value,
      };

      onChange('annotations', newValues);
    }
  );

  const deleteData = useHttpClient('', {
    options: {
      method: 'DELETE',
      loading: true,
      errorCatching: true,
    },
  });

  const deleteDataValues = useEventCallback((index: number) => {
    const newValues = (values?.annotations || [])
      .slice()
      .filter((_, indexCurrent) => indexCurrent !== index);

    if (newValues.length === 0) {
      onChanges({
        ...(values || {}),
        isSaved: false,
        annotations: [],
      });
    } else {
      onChange('annotations', newValues);
    }
  });

  const onDelete = useEventCallback((index: number) => {
    const idAnnotazione = (values?.annotations || [])[index]?.idAnnotazione;

    if (idAnnotazione) {
      // se i dati sono salvati nel DB effettuare pulizia
      (
        deleteData({
          url: `${__ACT_API_URL__}/dati-annotazione/rimuovi/${idAnnotazione}`,
        }) as ResponsePromise<void>
      ).then(() => {
        deleteDataValues(index);
      });

      return;
    }

    deleteDataValues(index);
  });

  const onClean = useEventCallback((index: number) => {
    const newValues = (values?.annotations || []).slice();

    newValues[index] = {
      idAnnotazione: newValues[index]?.idAnnotazione,
      isNotCertifiable: false,
      textContextualAnnotation: '',
      typeContextualAnnotation: '',
    };

    onChange('annotations', newValues);
  });

  const onSaveAnnotation = useEventCallback((index: number, valuesToSave?: Annotations) => {
    onSave({ ...(values || {}), annotations: [valuesToSave] } as SectionVM)?.then((res) => {
      const dataSaved = (res || undefined) as DataWizardAct | undefined;

      const newValues = (values?.annotations || []).slice();

      const datiAnnotazioneListCurrent = dataSaved?.datiAnnotazioneList
        ? dataSaved?.datiAnnotazioneList[0]
        : {};

      newValues[index] = {
        ...newValues[index],
        idAnnotazione: datiAnnotazioneListCurrent?.key?.idDatiAnnotazione,
        dateInsertion: datiAnnotazioneListCurrent.timestampInserimento
          ? DateTime.fromISO(datiAnnotazioneListCurrent.timestampInserimento).toFormat('dd/MM/yyyy')
          : '',
      };

      onChanges({
        ...(values || {}),
        isSaved: true,
        annotations: newValues,
      });
    });
  });

  const createAnnotation = useHttpClient('', {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const idAnscSubject: string = useMemo(() => {
    const valueIntNas: ParentVM = formContext.values[KeySubjects.keyIntNas] as ParentVM;

    if (!valueIntNas) {
      return '';
    }

    return valueIntNas.generality?.idAnsc || '';
  }, [formContext.values[KeySubjects.keyIntNas]]);

  const subjectIsSaved: boolean = useMemo(() => {
    const valueIntNas: ParentVM = formContext.values[KeySubjects.keyIntNas] as ParentVM;

    if (!valueIntNas) {
      return false;
    }

    return valueIntNas.isSaved || false;
  }, [formContext.values[KeySubjects.keyIntNas]]);

  const onClickCreateAnnotation = useEventCallback(() => {
    (
      createAnnotation({
        url: `${__ACT_API_URL__}/dati-annotazione/acquisizioneCittadinaza/${idEvento}/${idAnscSubject}`,
      }) as unknown as ResponsePromise<
        HttpResponse<{ annotazione?: string }>,
        HttpResponse<{ annotazione?: string }>
      >
    ).then((response) => {
      const castedResponse = response as AxiosResponse<HttpResponse<{ annotazione?: string }>>;

      const newValueAnnotation: Annotations = {
        isNotCertifiable: false,
        textContextualAnnotation: castedResponse.data.payload.annotazione || '',
        typeContextualAnnotation: '',
      };

      onChanges({
        contextualAnnotationTemplate: undefined,
        annotations: (values?.annotations || []).concat([
          newValueAnnotation,
        ]) as unknown as Array<Annotations>,
      });
    });
  });

  return (
    <Grid container spacing={2} item>
      {isAnncont312 ? (
        <Grid item xs={12} md={4}>
          <PmiTooltipError
            show={!Boolean(idAnscSubject) || !subjectIsSaved}
            message={
              !Boolean(idAnscSubject)
                ? "Ricerca atto non possibile: manca idAnsc dell'intestatario"
                : "Salvare l'intestatario inserito"
            }
          >
            <span>
              <PmiButton
                color="cdmBrandThird"
                variant="outlined"
                fullWidth
                onClick={onClickCreateAnnotation}
                disabled={
                  values?.annotations?.length === 1 || !Boolean(idAnscSubject) || !subjectIsSaved
                }
              >
                Crea annotazione servizio cittadinanza
              </PmiButton>
            </span>
          </PmiTooltipError>
        </Grid>
      ) : (
        <Fragment>
          <Grid item xs={12} md={8}>
            <ListTemplateAnnotations
              label="Formula annotazione contestuale"
              idDominio={isAnncont105 ? '105' : '104'}
              tipoEvento={tipoEvento || ''}
              withPropertyRegime
              value={values?.contextualAnnotationTemplate?.id?.toString() || ''}
              onChange={handleListTemplateTranscriptionChange}
              disabled={disabled}
              error={
                Boolean(errors?.contextualAnnotationTemplate) &&
                touched?.contextualAnnotationTemplate
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <IconButton
              onClick={onAddAnnotation}
              aria-label="Aggiungi annotazione"
              aria-haspopup="true"
              disabled={!values?.contextualAnnotationTemplate?.id}
            >
              <AddCircleOutline />
            </IconButton>
            <Typography color="secondary" display="inline-block">
              Aggiungi annotazione
            </Typography>
          </Grid>
        </Fragment>
      )}
      {values?.annotations?.map((itemAnnotation, index) => (
        <Grid item xs={12} key={`annotations-${index}`}>
          <FormAnnotation
            index={index}
            values={itemAnnotation}
            onChange={onChangeAnnotation}
            onSave={onSaveAnnotation}
            onDelete={onDelete}
            onClean={onClean}
            disabled={itemAnnotation.disabled}
            isAnncont312={isAnncont312}
          />
        </Grid>
      ))}
    </Grid>
  );
};

AnnotationsDataComponent.displayName = 'Form Annotations Data component';

const AnnotationsData = memo(AnnotationsDataComponent);

export default AnnotationsData;
